import React, { useCallback, useState } from 'react';
import { debounce } from 'lodash';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { currencyFormatter } from '../../utils/currencyFormat';
import { useGetProductsHandler } from '../../resources/products/dataHandlers/getProductsHandler';

const ProductSelect = ({
  label = 'Description',
  name,
  control,
  errors,
  readOnly = false,
  onProductSelect,
  onDescriptionChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const modifySearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 500),
    [],
  );

  const { products, isLoading } = useGetProductsHandler({
    searchQuery: {
      page: 1,
      limit: 10,
      description: searchTerm || undefined,
    },
  });

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box display="flex" alignItems="flex-start" gap={2}>
          <Autocomplete
            freeSolo
            loading={isLoading}
            {...field}
            id={name}
            fullWidth
            readOnly={readOnly}
            options={products}
            getOptionLabel={(option) => `${option?.description || ''}`}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box key={key} component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...optionProps}>
                  {option?.description || ''}{' '}
                  {option?.unit_rate != null ? ' | ' + currencyFormatter().format(option.unit_rate) : ''}
                </Box>
              );
            }}
            getOptionKey={(option) => option?._id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                multiline
                error={!!errors['description']}
                helperText={errors['description']?.message}
                onChange={(e) => onDescriptionChange(e.target.value)}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option._id === value?._id}
            onChange={(_, value) => {
              const finalValue = value === null ? null : typeof value === 'string' ? { description: value } : value;
              field.onChange(finalValue);
              if (onProductSelect) onProductSelect(finalValue);
            }}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') {
                modifySearchTerm(value);
              }
            }}
            value={field.value || null}
          />
        </Box>
      )}
    />
  );
};

export default ProductSelect;
